import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FiUpload, FiFile, FiImage, FiX, FiDownload } from 'react-icons/fi';

const FileUpload = ({ 
  files = [], 
  onFileChange, 
  onFileRemove,
  accept = {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    'application/pdf': ['.pdf']
  },
  maxFiles = 3,
  maxSize = 5242880, // 5MB
  label = 'Upload Files'
}) => {
  const [previews, setPreviews] = useState([]);

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: URL.createObjectURL(file)
    }));

    const totalFiles = files.length + newFiles.length;
    if (totalFiles > maxFiles) {
      alert(`Maksimal ${maxFiles} file`);
      return;
    }

    // Convert to base64 for localStorage
    const readers = newFiles.map((fileObj) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            ...fileObj,
            data: reader.result,
            preview: reader.result // Use base64 for preview
          });
        };
        reader.readAsDataURL(fileObj.file);
      });
    });

    Promise.all(readers).then((base64Files) => {
      onFileChange([...files, ...base64Files]);
    });
  }, [files, onFileChange, maxFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    maxFiles
  });

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <FiImage className="text-blue-500" size={24} />;
    if (type === 'application/pdf') return <FiFile className="text-red-500" size={24} />;
    return <FiFile className="text-gray-500" size={24} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleRemove = (id) => {
    const newFiles = files.filter(f => f.id !== id);
    onFileRemove(id);
    onFileChange(newFiles);
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-[var(--text-secondary)]">
        {label}
      </label>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive 
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
            : 'border-[var(--border-color)] hover:border-blue-500 hover:bg-[var(--bg-secondary)]'
          }`}
      >
        <input {...getInputProps()} />
        <FiUpload className="mx-auto text-[var(--text-secondary)]" size={40} />
        <p className="mt-2 text-sm text-[var(--text-secondary)]">
          {isDragActive ? 'Drop files here...' : 'Drag & drop files here, or click to select'}
        </p>
        <p className="text-xs text-[var(--text-secondary)] mt-1">
          Maksimal {maxFiles} file, ukuran max {formatFileSize(maxSize)}
        </p>
        <p className="text-xs text-[var(--text-secondary)]">
          Supported: Images (JPG, PNG, GIF, WebP) & PDF
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-3 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]"
            >
              <div className="flex items-center space-x-3">
                {/* Thumbnail for images */}
                {file.type && file.type.startsWith('image/') && file.data && (
                  <img
                    src={file.data}
                    alt={file.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                )}
                {!file.type?.startsWith('image/') && getFileIcon(file.type)}
                
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)] truncate max-w-xs">
                    {file.name}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {/* Preview button for images */}
                {file.type && file.type.startsWith('image/') && (
                  <a
                    href={file.data}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                    title="Preview"
                  >
                    <FiDownload size={18} />
                  </a>
                )}
                {/* Remove button */}
                <button
                  onClick={() => handleRemove(file.id)}
                  className="p-1 text-red-600 hover:text-red-800 transition-colors"
                  title="Remove"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* File count */}
      {files.length > 0 && (
        <p className="text-xs text-[var(--text-secondary)]">
          {files.length} of {maxFiles} files uploaded
        </p>
      )}
    </div>
  );
};

export default FileUpload;